import React, { useState, useRef } from 'react';
import axios from 'axios';
import { Headphones, Loader2, Pause, Play } from 'lucide-react';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

export default function PodcastPlayer({ specId, courseId, courseName, summary }) {
  const [podcast, setPodcast] = useState(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const [isPlaying, setIsPlaying] = useState(false);
  const audioRef = useRef(null);

  const generatePodcast = async () => {
    setLoading(true);
    setError(null);
    try {
      const response = await axios.post(`${API}/podcast/generate`, null, {
        params: {
          spec_id: specId || 'education',
          course_id: courseId,
          voice: 'nova'
        }
      });
      setPodcast(response.data);
    } catch (e) {
      console.error('Failed to generate podcast:', e);
      setError('שגיאה ביצירת הפודקאסט');
    } finally {
      setLoading(false);
    }
  };

  const togglePlay = () => {
    if (!audioRef.current) return;
    
    if (isPlaying) {
      audioRef.current.pause();
    } else {
      audioRef.current.play();
    }
    setIsPlaying(!isPlaying);
  };

  return (
    <div className="bg-gradient-to-br from-purple-600 to-indigo-700 text-white p-8 sm:p-10 rounded-3xl shadow-xl">
      <div className="flex items-center gap-3 mb-6">
        <div className="w-12 h-12 bg-white/20 rounded-2xl flex items-center justify-center">
          <Headphones className="w-6 h-6 text-white" />
        </div>
        <div>
          <h3 className="text-2xl font-black">פודקאסט סיכום</h3>
          <p className="text-purple-200 text-sm">האזן לסיכום הקורס בקול AI</p>
        </div>
      </div>

      {!podcast && !loading && (
        <div className="text-center py-8">
          <div className="text-6xl mb-4">🎙️</div>
          <p className="text-purple-200 mb-6">יצירת פודקאסט AI מותאם אישית לקורס זה</p>
          <button
            onClick={generatePodcast}
            className="bg-white text-purple-700 px-8 py-4 rounded-2xl font-black text-lg hover:bg-purple-100 transition-all active:scale-95"
            data-testid="generate-podcast-button"
          >
            צור פודקאסט 🎧
          </button>
        </div>
      )}

      {loading && (
        <div className="text-center py-8">
          <Loader2 className="w-12 h-12 animate-spin mx-auto mb-4 text-purple-200" />
          <p className="text-purple-200">יוצר את הפודקאסט... זה יכול לקחת עד 30 שניות</p>
        </div>
      )}

      {error && (
        <div className="text-center py-8">
          <p className="text-rose-300 mb-4">{error}</p>
          <button
            onClick={generatePodcast}
            className="bg-white/20 text-white px-6 py-3 rounded-xl font-bold hover:bg-white/30 transition-all"
          >
            נסה שוב
          </button>
        </div>
      )}

      {podcast && (
        <div className="space-y-6">
          {/* Audio Player */}
          {podcast.audio_url && (
            <div className="bg-white/10 p-6 rounded-2xl">
              <div className="flex items-center gap-4">
                <button
                  onClick={togglePlay}
                  className="w-16 h-16 bg-white rounded-full flex items-center justify-center shadow-lg hover:scale-105 transition-transform"
                  data-testid="play-podcast-button"
                >
                  {isPlaying ? (
                    <Pause className="w-8 h-8 text-purple-700" />
                  ) : (
                    <Play className="w-8 h-8 text-purple-700 mr-[-3px]" />
                  )}
                </button>
                <div className="flex-1">
                  <h4 className="font-bold text-lg">סיכום: {courseName}</h4>
                  <p className="text-purple-200 text-sm">קול: נובה • פודקאסט AI</p>
                </div>
              </div>
              <audio
                ref={audioRef}
                src={podcast.audio_url}
                onEnded={() => setIsPlaying(false)}
                className="hidden"
              />
            </div>
          )}

          {/* Script Content */}
          {podcast.content && (
            <div className="space-y-4">
              <h4 className="font-bold text-lg text-purple-200">תוכן הפודקאסט:</h4>
              
              {podcast.content.intro && (
                <div className="bg-white/10 p-4 rounded-xl">
                  <span className="text-xs font-bold text-purple-300 uppercase">פתיחה</span>
                  <p className="text-white mt-2">{podcast.content.intro}</p>
                </div>
              )}

              {podcast.content.key_points && (
                <div className="bg-white/10 p-4 rounded-xl">
                  <span className="text-xs font-bold text-purple-300 uppercase">נקודות עיקריות</span>
                  <ul className="mt-2 space-y-2">
                    {podcast.content.key_points.map((point, i) => (
                      <li key={i} className="flex items-start gap-2">
                        <span className="text-purple-300">•</span>
                        <span>{point}</span>
                      </li>
                    ))}
                  </ul>
                </div>
              )}

              {podcast.content.conclusion && (
                <div className="bg-white/10 p-4 rounded-xl">
                  <span className="text-xs font-bold text-purple-300 uppercase">סיכום</span>
                  <p className="text-white mt-2">{podcast.content.conclusion}</p>
                </div>
              )}
            </div>
          )}

          {!podcast.has_audio && (
            <div className="bg-amber-500/20 border border-amber-400/30 p-4 rounded-xl text-center">
              <p className="text-amber-200 text-sm">
                ⚠️ יצירת האודיו נכשלה - ניתן לקרוא את התוכן למעלה
              </p>
            </div>
          )}
        </div>
      )}
    </div>
  );
}
